/**
 * Name utilities — surname-first display, first/last splitting.
 *
 * The canonical first/last split lives on the roster table
 * (migration 07_roster_names_split.sql). We read from `state.roster[name]`
 * when available, with a "split on last whitespace" fallback for any
 * name not yet backfilled (typically a fresh hire).
 */
import { state } from "../state/store";
import type { RosterMap } from "../types";

/** Returns [firstName, surname] for a full name. Empty surname for
 *  single-token names. Compound surnames preserved when the roster row
 *  has been backfilled by migration 07. */
export function splitName(fullName: string, roster?: RosterMap): [string, string] {
  const k = String(fullName ?? "").trim();
  if (!k) return ["", ""];
  const r = (roster ?? state.value.roster)[k];
  if (r && r.first_name && r.last_name) return [r.first_name, r.last_name];
  const parts = k.split(/\s+/);
  if (parts.length <= 1) return [parts[0] ?? "", ""];
  const surname = parts[parts.length - 1] ?? "";
  return [parts.slice(0, -1).join(" "), surname];
}

export function displaySurname(name: string): string {
  const [first, surname] = splitName(name);
  return surname || first;
}

export function displayFirst(name: string): string {
  return splitName(name)[0];
}

/** "Surname FirstName" — company-wide convention. */
export function displayName(name: string): string {
  const [first, surname] = splitName(name);
  return surname ? `${surname} ${first}` : first;
}

/** Comparator for surname-first sort. localeCompare handles accented
 *  characters consistently across "Lóránt" / "Lorenzo" / "Luca". */
export function bySurname(a: string, b: string): number {
  const la = displaySurname(a).toLowerCase();
  const lb = displaySurname(b).toLowerCase();
  const cmp = la.localeCompare(lb);
  if (cmp !== 0) return cmp;
  return displayFirst(a).toLowerCase().localeCompare(displayFirst(b).toLowerCase());
}

/** Default email derivation for a name. Used as a placeholder when a
 *  roster row hasn't been imported yet. Existing employees get their
 *  real email from state.roster[name].email.
 *
 *  Corporate convention: `name.surname@icons.it`
 *    - Multi-word first names → joined by ".":  Sofia Giulia → "sofia.giulia"
 *    - Multi-word surnames    → collapsed:      Di Fini      → "difini"
 *    - Final email            → first.surname:  daniele.difini@icons.it
 *
 *  We split the canonical first/surname via splitName() (which prefers the
 *  roster's first_name / last_name when available, falling back to a
 *  last-whitespace heuristic). Apostrophes + accented characters are
 *  ASCII-folded before joining. */
function fold(s: string): string {
  return String(s ?? "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[àáâä]/g, "a")
    .replace(/[èéêë]/g, "e")
    .replace(/[ìíîï]/g, "i")
    .replace(/[òóôö]/g, "o")
    .replace(/[ùúûü]/g, "u")
    .replace(/[ñ]/g, "n")
    .replace(/[ç]/g, "c");
}
export function nameToEmail(name: string, domain: string): string {
  const [first, surname] = splitName(name);
  // First names join with "." so a person with two given names ends up as
  // "first.second.surname@icons.it"
  const firstSlug = fold(first).trim().split(/\s+/).filter(Boolean).join(".");
  // Surname collapses internal whitespace so "Di Fini" → "difini" and
  // "Finzi Longo" → "finzilongo", matching the iCons mailbox convention.
  const lastSlug = fold(surname).trim().replace(/\s+/g, "");
  const local = [firstSlug, lastSlug].filter(Boolean).join(".");
  return `${local}@${domain}`;
}

/** Surname-sorted list of active people from the roster. */
export function activePeople(roster: RosterMap): string[] {
  return Object.keys(roster)
    .filter((n) => roster[n]?.active !== false)
    .sort(bySurname);
}

/** People to render in the Company Overview for a given visible month.
 *  Includes:
 *    - all currently-active employees, AND
 *    - former employees whose `last_active_day` is in or after the
 *      visible month (so they show in months where they had data
 *      but vanish from months entirely after their departure).
 *
 *  `visibleYearMonth` is "YYYY-MM" (string compare is sufficient since
 *  the format is left-padded). */
export function peopleForMonth(roster: RosterMap, visibleYearMonth: string): string[] {
  return Object.keys(roster)
    .filter((n) => {
      const e = roster[n];
      if (!e) return false;
      if (e.active !== false) return true;
      // Former employee: keep them visible up to the month containing
      // their last recorded day. last_active_day is null only for very
      // old un-backfilled rows — treat those as "show in all months"
      // so we don't accidentally hide historical data.
      if (!e.last_active_day) return true;
      const departureYearMonth = e.last_active_day.slice(0, 7);
      return visibleYearMonth <= departureYearMonth;
    })
    .sort(bySurname);
}
